import React, { useState } from 'react';
import { X, Search, Plus, ChevronRight, User, AlertCircle, ChevronDown } from 'lucide-react';
import { STAGES, MANAGERS, EVENT_TYPES } from '../../lib/constants';
import ContactModal from './ContactModal';
import { contactService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import { toLocalDateString } from '../../utils/helpers';

export default function NewLeadModal({ onClose, onSave, managers, contacts, onAddContact, sources, eventTypes, currentUser }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [localError, setLocalError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [showContactCreator, setShowContactCreator] = useState(false);
  const [formData, setFormData] = useState({
    manager: currentUser?.role !== 'Admin' && currentUser?.role !== 'Owner' ? currentUser?.name || '' : '',
    stage: 'New',
    guests: '',
    clientBudget: '',
    eventType: '',
    eventDate: '',
    inquiryDate: toLocalDateString(new Date()),
    notes: '',
    source: ''
  });

  // Async search state
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced search effect
  React.useEffect(() => {
    const search = async () => {
      if (!contactSearch) {
        setSearchedContacts([]);
        return;
      }
      setSearching(true);
      try {
        const result = await contactService.getAll({ limit: 5, search: contactSearch });
        setSearchedContacts(result.data);
      } catch (e) {
        console.error(e);
      } finally {
        setSearching(false);
      }
    };

    const timer = setTimeout(search, 400);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  const displayContacts = contactSearch ? searchedContacts : (contacts || []).slice(0, 5);

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setStep(2);
  };

  const handleCreateContact = async (data) => {
    const newContact = await onAddContact(data);
    if (newContact) {
      setSelectedContact(newContact);
      setShowContactCreator(false);
      setStep(2);
    }
  };

  const handleSubmit = () => {
    if (!selectedContact) {
      setLocalError(t('contactRequired') || 'Contact is required.');
      return;
    }

    setLocalError(null);

    onSave({
      ...formData,
      contactId: selectedContact.id,
      clientName: `${selectedContact.firstName} ${selectedContact.lastName || ''}`.trim(),
      phone: selectedContact.phone,
      quotationAmount: 0,
      clientBudget: Number(formData.clientBudget) || 0,
      guests: Number(formData.guests) || null
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {showContactCreator && (
        <ContactModal
          onClose={() => setShowContactCreator(false)}
          onSave={handleCreateContact}
        />
      )}
      <div className="bg-white w-full max-w-xl max-h-[95vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h3 className="font-bold text-gray-800">
            {step === 1 ? t('step1SelectContact') : t('step2LeadDetails')}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {localError && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{localError}</span>
            <button onClick={() => setLocalError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="p-6 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {t('leadContactDescription')}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  className="w-full pl-10 p-3 border rounded-xl bg-gray-50 outline-none"
                  placeholder={t('searchByNameOrPhone')}
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searching && <p className="text-center text-xs text-gray-500">{t('searching')}</p>}
                {displayContacts.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => handleContactSelect(c)}
                    className="flex items-center justify-between p-3 hover:bg-blue-50 rounded-lg cursor-pointer border border-transparent hover:border-blue-100 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
                        {c.firstName[0]}
                      </div>
                      <div>
                        <p className="font-bold text-gray-800 text-sm">
                          {c.firstName} {c.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{c.phone}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
                {!searching && displayContacts.length === 0 && contactSearch && (
                  <p className="text-center text-sm text-gray-400 py-2">{t('noContacts')}</p>
                )}
              </div>
              <button
                onClick={() => setShowContactCreator(true)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('createNewContact')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-blue-800 text-sm">
                    {selectedContact.firstName} {selectedContact.lastName}
                  </span>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-xs text-blue-600 underline"
                >
                  {t('change')}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('clientBudget')}</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-lg mt-1"
                    value={formData.clientBudget}
                    onChange={(e) => setFormData({ ...formData, clientBudget: e.target.value })}
                    placeholder={t('clientBudgetPlaceholder')}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('guests') || 'Guests'}</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-lg mt-1"
                    value={formData.guests}
                    onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                    placeholder={t('guestsPlaceholder') || 'Enter number of guests'}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('stage')}</label>
                  <Dropdown>
                    <DropdownTrigger>
                      <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[42px]">
                        {formData.stage}
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                    </DropdownTrigger>
                    <DropdownPopover className="w-full">
                      <DropdownMenu
                        selectedKeys={new Set([formData.stage])}
                        selectionMode="single"
                        onSelectionChange={(keys) => setFormData({ ...formData, stage: Array.from(keys)[0] })}
                      >
                        {STAGES.map((s) => (
                          <DropdownItem key={s} id={s}>
                            {s}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </DropdownPopover>
                  </Dropdown>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('eventType')}</label>
                  <Dropdown>
                    <DropdownTrigger>
                      <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[42px]">
                        {formData.eventType || t('selectEventType')}
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                    </DropdownTrigger>
                    <DropdownPopover className="w-full">
                      <DropdownMenu
                        selectedKeys={new Set([formData.eventType])}
                        selectionMode="single"
                        onSelectionChange={(keys) => setFormData({ ...formData, eventType: Array.from(keys)[0] })}
                      >
                        {([...new Set(eventTypes || EVENT_TYPES)]).map((et) => {
                          const etName = typeof et === 'string' ? et : et.name;
                          return (
                            <DropdownItem key={etName} id={etName}>
                              {etName}
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </DropdownPopover>
                  </Dropdown>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentUser?.role === 'Admin' || currentUser?.role === 'Owner' ? (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">{t('manager')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[42px]">
                          {formData.manager || (t('autoAssign') || 'Auto-Assign (Based on Rules)')}
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full">
                        <DropdownMenu
                          selectedKeys={new Set([formData.manager])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setFormData({ ...formData, manager: Array.from(keys)[0] })}
                        >
                          <DropdownItem id="">{t('autoAssign') || 'Auto-Assign'}</DropdownItem>
                          {([...new Set(managers || MANAGERS)]).filter(m => m !== 'Unassigned').map((m) => (
                            <DropdownItem key={m} id={m}>
                              {m}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">{t('manager')}</label>
                    <div className="w-full p-2 border rounded-lg mt-1 bg-gray-100 text-gray-700 font-medium">
                      {currentUser?.name} (Auto-assigned)
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('source')}</label>
                  <Dropdown>
                    <DropdownTrigger>
                      <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[42px]">
                        {formData.source || t('selectSource')}
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </button>
                    </DropdownTrigger>
                    <DropdownPopover className="w-full">
                      <DropdownMenu
                        selectedKeys={new Set([formData.source])}
                        selectionMode="single"
                        onSelectionChange={(keys) => setFormData({ ...formData, source: Array.from(keys)[0] })}
                      >
                        {sources?.length ? sources.map((s) => (
                          <DropdownItem key={s.id} id={s.name}>
                            {s.name}
                          </DropdownItem>
                        )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                      </DropdownMenu>
                    </DropdownPopover>
                  </Dropdown>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">{t('eventDate')}</label>
                  <DatePicker
                    className="mt-1"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    placement="top"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    {t('inquiryDate')}
                  </label>
                  <DatePicker
                    className="mt-1"
                    value={formData.inquiryDate}
                    onChange={(e) => setFormData({ ...formData, inquiryDate: e.target.value })}
                    placement="top"
                    align="right"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">{t('notes')}</label>
                <textarea
                  className="w-full p-2 border rounded-lg mt-1 h-20"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2 shrink-0">
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
              >
                {t('back')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold"
              >
                {t('createLead')}
              </button>
            </>
          )}
          {step === 1 && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
            >
              {t('cancel')}
            </button>
          )}
        </div>
      </div>
    </div >
  );
}
